import gql from "graphql-tag";
import { BuildQueryResult } from "ra-data-graphql";
import { QueryTypes } from "../../constants";

interface Member {
  id: string;
  disabled: boolean;
  created: number;
  user: {
    id: string;
    displayName: string;
    email: string;
  };
  status: {
    displayName: string;
    invitedAt: number;
  };
  role: {
    displayName: string;
  };
  org: {
    id: string;
    displayName: string;
  };
}

interface BuildQueryMemberParams {
  id: string;
  meta: {
    nextPageCursor: string;
  };
  data: {
    orgId: string;
    email: string;
    displayName: string;
    defaultLoginPortal: string;
    roleName: string;

    membershipId: string;
    disabled: boolean;
  };
}

// Every query that can populate react-admin's record cache must select the same
// fields. A query with a narrower selection set (e.g. one missing `org`) primes
// the cache with a partial record, and because App.tsx sets a five minute
// `staleTime` that partial record is then served to the edit page without a
// refetch. See https://runelabs.atlassian.net/browse/SW-10141.
const membershipFields = `
  id
  disabled
  created
  user {
    id
    displayName
    email
  }
  status {
    displayName
    invitedAt
  }
  role {
    displayName
  }
  org {
    id
    displayName
  }
`;

const buildQueryMember = (
  fetchType: string,
  params: BuildQueryMemberParams
): BuildQueryResult => {
  switch (fetchType) {
    case QueryTypes.GET_ONE:
      return {
        query: gql`
          query ($membershipId: ID!) {
            membership(membershipId: $membershipId) {
              ${membershipFields}
            }
          }
        `,
        variables: { membershipId: params.id },
        parseResponse: (response) => {
          return {
            data: response.data.membership
          };
        }
      };
    case QueryTypes.GET_MANY_REFERENCE:
      return {
        query: gql`
          query ($orgId: ID!, $cursor: Cursor) {
            org(orgId: $orgId) {
              membershipList(withDisabled: true, cursor: $cursor) {
                memberships {
                  ${membershipFields}
                }
                pageInfo {
                  endCursor
                }
              }
            }
          }
        `,
        // TODO Implement pagination.
        variables: { orgId: params.id, cursor: params.meta.nextPageCursor },
        parseResponse: (response) => {
          return {
            data: response.data.org.membershipList.memberships.map(
              (member: Member) => ({
                ...member
              }),
              params
            ),
            total: response.data.org.membershipList.memberships.length,
            endCursor: response.data.org.membershipList.pageInfo.endCursor
          };
        }
      };
    case QueryTypes.CREATE:
      return {
        query: gql`
          mutation ($input: CreateMembershipInput!) {
            createMembership(input: $input) {
              ${membershipFields}
            }
          }
        `,
        variables: {
          input: {
            orgId: params.data.orgId,
            email: params.data.email,
            displayName: params.data.displayName,
            roleName: params.data.roleName
          }
        },
        parseResponse: (response) => {
          return { data: response.data.createMembership };
        }
      };
    case QueryTypes.UPDATE:
      return {
        query: gql`
          mutation ($input: UpdateMembershipInput!) {
            updateMembership(input: $input) {
              ${membershipFields}
            }
          }
        `,
        variables: {
          input: {
            membershipId: params.data.membershipId,
            disabled: params.data.disabled,
            roleName: params.data.roleName
          }
        },
        parseResponse: (response) => {
          return { data: response.data.updateMembership };
        }
      };
  }
  throw Error(`unknown fetch type ${fetchType}`);
};

export default buildQueryMember;
